import { ThunkDispatch } from "core-integration/src/store";
import { updateElementControls } from "core-integration/src/store/elements/actions";
import {
  getElementControls,
  getElementsFromControls,
} from "core-integration/src/store/elements/reducers";
import {
  showElementSummary,
  updateElementControlsAndMaxCost,
} from "core-integration/src/store/elements/thunks";
import { IElement } from "core-integration/src/store/elements/types";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { integerToMoney } from "core-integration/src/utils/money";
import React, { useEffect, useState } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Box } from "rebass/styled-components";
import styled, { css } from "styled-components";
import i18n from "../../i18n";
import { integerToDecimal } from "../../utils/decimal";
import { integerDivide } from "../../utils/points";
import { getElementStatValue, getStatDetails } from "../../utils/stats";
import { createPageViewData } from "../../utils/tealium";
import Alert from "../Alert";
import ElementFilter from "../element-controls/ElementFilter";
import ElementSort from "../element-controls/ElementSort";
import Paginator, { paginate } from "../element-controls/Paginator";
import ElementDialogButton from "../ElementDialogButton";
import { ElementRow, ElementTable } from "../ElementTable";
import { FieldCol, FieldCols } from "../FieldRenderers";
import { Main, Wrapper } from "../Layout";
import Media from "../Media";
import Shirt from "../Shirt";
import { ScrollTable } from "../Table";
import Tooltip, { TooltipLabel } from "../Tooltip";
import { Ellipsis, VisuallyHidden } from "../Utils";

const StatsStatus = styled.th`
  width: 10%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 7%;
  }
`;

const StatsElement = styled.th`
  width: 40%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 38%;
  }
`;

const StatsCost = styled.th`
  width: 10%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsSel = styled.th`
  width: 10%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsForm = styled.th`
  width: 10%;
  white-space: normal;
  overflow-wrap: break-word;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsPts = styled.th`
  width: 10%;
  white-space: normal;
  overflow-wrap: break-word;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const StatsExtra = styled.th`
  width: 10%;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 11%;
  }
`;

const ElementNameButton = styled.button`
  width: 100%;
  padding: 0;
  border: 0;
  background-color: transparent;
  color: inherit;
  font-weight: bold;
  text-align: left;
  cursor: pointer;
`;

const Name = styled.div`
  color: ${({ theme }) => theme.colors.primary};

  &:hover {
    text-decoration: underline;
  }
`;

const Team = styled.div`
  margin-inline-end: ${({ theme }) => theme.space[2]};
  font-size: ${({ theme }) => theme.fontSizes[1]};
  ${
    i18n.language === "tr" &&
    css`
      text-transform: lowercase;
      &::first-letter {
        text-transform: capitalize;
      }
    `
  }
`;

type Props = WithTranslation;

const Statistics = ({ t }: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const { statName } = useParams();
  const [page, setPage] = useState<number>(1);
  const controls = useSelector(getElementControls);
  const currencyDivisor = 10;
  const elements = useSelector(getElementsFromControls);
  const teamsById = useSelector(getTeamsById);

  const showElementDialog = (elementId: number) =>
    dispatch(showElementSummary(elementId));

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPage(1);
    dispatch(
      updateElementControls({
        ...controls,
        filter: e.target.value,
      }),
    );
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPage(1);
    dispatch(
      updateElementControls({
        ...controls,
        sort: e.target.value,
      }),
    );
  };

  const defaultStats: Array<keyof IElement> = [
    "now_cost",
    "selected_by_percent",
    "form",
    "total_points",
  ];

  useEffect(() => {
    dispatch(
      updateElementControlsAndMaxCost({
        ...controls,
        filter: "all",
        sort: statName || "total_points",
        search: "",
      }),
    );
    createPageViewData("statistics");
  }, []);

  const { data, totalPages } = paginate(elements.data, page, 30);
  const extraStat =
    defaultStats.indexOf(controls.sort) === -1 ? controls.sort : "";
  const statDetail = getStatDetails(String(controls.sort), t);

  return (
    <Wrapper>
      <Main>
        <VisuallyHidden as="div">
          <h2>{t("statistics.title", "Statistics")}</h2>
        </VisuallyHidden>
        <form>
          <FieldCols>
            <FieldCol>
              <ElementFilter handleFilterChange={handleFilterChange} />
            </FieldCol>
            <FieldCol>
              <ElementSort handleSortChange={handleSortChange} />
            </FieldCol>
          </FieldCols>
        </form>
        <Box mb="4">
          <Alert type="info" textAlign="start">
            {statDetail ? statDetail.description : ""}
          </Alert>
        </Box>
        <ScrollTable>
          <ElementTable>
            <thead>
              <tr>
                <StatsStatus>&nbsp;</StatsStatus>
                <StatsElement>{t("statistics.player", "Player")}</StatsElement>
                <StatsCost>$</StatsCost>
                <StatsSel>
                  <Tooltip
                    content={t("statistics.selectedByLong", "Selected by %")}
                  >
                    <TooltipLabel>
                      {t("statistics.selectedByShort", "Sel.")}
                    </TooltipLabel>
                  </Tooltip>
                </StatsSel>
                <StatsForm>{t("statistics.form", "Form")}</StatsForm>
                <StatsPts>
                  <Tooltip
                    content={t("statistics.totalPoints", "Total points")}
                  >
                    <TooltipLabel>
                      {t("statistics.totalPointsShort", "TP.")}
                    </TooltipLabel>
                  </Tooltip>
                </StatsPts>
                {extraStat ? <StatsExtra>**</StatsExtra> : null}
              </tr>
            </thead>
            <tbody>
              {data.map((e) => (
                <ElementRow key={e.id}>
                  <td>
                    <ElementDialogButton elementId={e.id} variant="list" />
                  </td>
                  <td>
                    <ElementNameButton onClick={() => showElementDialog(e.id)}>
                      <Media
                        img={<Shirt elementId={e.id} width="50px" />}
                        centred={true}
                      >
                        <Name>
                          <Ellipsis>
                            {e.first_name} {e.second_name}
                          </Ellipsis>
                        </Name>
                        <Team>{teamsById[e.team].short_name}</Team>
                      </Media>
                    </ElementNameButton>
                  </td>
                  <td>{integerToMoney(e.now_cost, currencyDivisor)}</td>
                  <td>{e.selected_by_percent}%</td>
                  <td>{integerToDecimal(Number(e.form), 10)}</td>
                  <td>{integerDivide(e.total_points, 10)}</td>
                  {extraStat ? (
                    <td>
                      {getElementStatValue(e, extraStat, currencyDivisor)}
                    </td>
                  ) : null}
                </ElementRow>
              ))}
            </tbody>
          </ElementTable>
        </ScrollTable>
        <Paginator totalPages={totalPages} page={page} setPage={setPage} />
      </Main>
    </Wrapper>
  );
};

export default withTranslation()(Statistics);
